import * as React from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Rating,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useNavigate } from "react-router-dom";
import { Company } from "../../types/company";
import MapPreview from "../../components/MapPreview";
import Linkify from "../../components/Linkify";
import { Edit, Star } from "@mui/icons-material";
import { useGetCompanyByIdQuery } from "../../services/company.service";
import { formatMoney } from "../../utils/helper";
import { GlassDrawer, OrangeLinearProgress } from "../../components/global";

interface CompanyDrawerProps {
  data: Company;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  forceRefresh?: boolean;
}

export default function CompanyDrawer(props: CompanyDrawerProps) {
  const { data: dataProp, open, setOpen, forceRefresh = false } = props;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = React.useState(dataProp);

  const {
    data: dataApi,
    isError,
    isLoading,
    isFetching,
  } = useGetCompanyByIdQuery(dataProp?.id!, {
    skip: !forceRefresh || !dataProp,
    refetchOnMountOrArgChange: true,
  });

  React.useEffect(() => {
    if (!!dataApi && !isError) setData(dataApi);
    else setData(dataProp);
  }, [dataApi, dataProp, isError]);

  const [lng, lat] = React.useMemo(
    () => data?.coordinate?.coordinates || [],
    [data]
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <GlassDrawer
      anchor="right"
      open={open}
      onClose={handleClose}
      title={data?.name}
      contentProps={{
        sx: {
          gap: 0,
        },
      }}
    >
      {isMobile && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <IconButton onClick={handleClose} size="small">
            <ClearIcon />
          </IconButton>
        </Box>
      )}
      {isLoading || isFetching ? <OrangeLinearProgress position="relative" sx={{ mb: 2 }} /> : null}
      <Box sx={{ mb: 2 }}>
        <MapPreview lat={lat} lng={lng} height="300px" />
      </Box>
      <List>
        <ListItem alignItems="flex-start">
          <ListItemText
            primary={`${data?.name || ""}`}
            primaryTypographyProps={{ fontWeight: "500", fontSize: 18 }}
            secondary={
              <>
                {data?.city}
                {data?.state ? `, ${data.state}` : ""}
              </>
            }
            sx={{ flex: 1 }}
          />
        </ListItem>
        <ListItem>
          <Rating
            value={data?.total_rating || 0}
            readOnly
            precision={0.5}
            emptyIcon={
              <Star style={{ opacity: 0.55 }} fontSize="inherit" />
            }
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
              <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                HQ Address
              </Typography>
            }
            secondary={`${data?.headquarters_address_1}, ${data?.city}, ${data?.zip}`}
          />
        </ListItem>
        {/* <ListItem>
                <ListItemText
                  primary={`Billing Address`}
                  primaryTypographyProps={{ fontWeight: "500" }}
                  secondary={`${data?.billing_address_1}, ${data?.billing_city}, ${data?.billing_zip}`}
                />
              </ListItem> */}
        <ListItem>
          <ListItemText
            primary={
              <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                Phone number
              </Typography>
            }
            secondary={`${data?.phone_number}`}
          />
        </ListItem>
        {/* <ListItem>
                <ListItemText primary={`Email`} secondary={`${data?.email}`} />
              </ListItem> */}
        <ListItem>
          <ListItemText
            primary={
              <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                Balance
              </Typography>
            }
            secondary={`${formatMoney(data?.balance_decrypt)}`}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
              <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                Uplift
              </Typography>
            }
            secondary={`${data?.percentage_uplift || 0}%`}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
              <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                Socials
              </Typography>
            }
            secondary={
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: 14 }}>
                  LinkedIn: <Linkify url={data?.linked_in} />
                </Typography>
                <Typography sx={{ fontSize: 14 }}>
                  Facebook: <Linkify url={data?.facebook} />
                </Typography>
                <Typography sx={{ fontSize: 14 }}>
                  Instagram: <Linkify url={data?.instagram} />
                </Typography>
                <Typography sx={{ fontSize: 14 }}>
                  Website: <Linkify url={data?.website} />
                </Typography>
              </Box>
            }
          />
        </ListItem>
      </List>
      <Box display="flex" justifyContent="center" sx={{ mt: 5, gap: 2, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          onClick={() => navigate(`/clients/${data?.id}`)}
          sx={{
            backgroundColor: "#F4A259",
            color: "#ffffff",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#E8944A",
            },
          }}
        >
          More Detail
        </Button>
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={() => navigate(`/clients/${data?.id}/edit`)}
          sx={{
            backgroundColor: "#F4A259",
            color: "#ffffff",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#E8944A",
            },
          }}
        >
          Edit Client
        </Button>
      </Box>
    </GlassDrawer>
  );
}
